import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  Typography,
  Input,
  Select,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import MainContainer from "../../layout/MainContainer";
import { useNavigate, useParams } from "react-router-dom";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";

import {
  usePostAddCategoryMutation,
  useLazyGetSpecificCategoryByIdQuery,
  useEditCategoryMutation,
  useLazyGetAllCategoryQuery,
} from "../../services/category";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { errorToast, successToast, warnToast } from "../../helpers";
import { UploadMedia, UploadVideo } from "../../utils/uploadMedia";
import Loader from "../../helpers/constants/Loader";
import { isNumber } from "../../utils/validation";
import {
  useAddReelsMutation,
  useUpdateReelMutation,
} from "../../services/reels";

const AddReels = () => {
  const { _id } = useParams();
  const navigate = useNavigate();
  const [profilePicture, setProfilePicture] = useState("");
  const [fetchApi, { data }] = useLazyGetSpecificCategoryByIdQuery();
  const [addReel, { isLoading }] = useAddReelsMutation();
  const [updateReel] = useUpdateReelMutation();

  const [Loading, setIsLoading] = useState<boolean>(false);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    try {
      if (!file) {
        setProfilePicture("");
        // setImageError("Image is required");
        return;
      }
      setIsLoading(true);
      const res = await UploadVideo(file);
      if (res?.statusCode === 200) {
        const imageUrl = res?.data[0]?.location;
        setProfilePicture(imageUrl);
        successToast("Reel uploaded successfully.");
      } else {
        errorToast(res?.message);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error uploading image:", error);
      errorToast("Error uploading image. Please try again.");
    }
  };

  const fetchDataById = async (id: any) => {
    try {
      const response = await fetchApi({ userId: id }).unwrap();
      if (response?.statusCode === 200) {
        setProfilePicture(response?.data?.image || "");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      short_name: "",
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required("Title is required")
        .min(3, "Minimum 3 characters are required"),
      short_name: Yup.string().required("Description is required"),
    }),
    onSubmit: async (values) => {
      const body = {
        title: values.title,
        reel: profilePicture,
        description: values.short_name,
      };
      if (!_id)
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addReel({ body: encryptedBody }).unwrap();
          if (response.statusCode === 200) {
            successToast(response.data.message || "Reel added Successfully");
            navigate("/manage-reels/");
          } else {
            errorToast("error");
          }
        } catch (e: any) {
          errorToast(e?.data?.message);
        }
      else {
        try {
          const encryptedBody2 = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateReel({
            id: _id,
            body: encryptedBody2,
          }).unwrap();
          if (response.statusCode === 200) {
            successToast("Reel Updated Successfully");
            navigate("/manage-reels/");
          }
        } catch (e: any) {
          errorToast(e?.message || e?.data?.message);
        }
      }
    },
  });

  useEffect(() => {
    if (data && data.statusCode === 200) {
      formik.setValues({
        title: data?.data?.name || "",
        short_name: data?.data?.short_name || "",
      });
    }
  }, [data]);

  useEffect(() => {
    if (_id) {
      fetchDataById(_id);
    }
  }, [_id]);

  return (
    <>
      <div>
        <Loader isLoad={isLoading || Loading} />
        <div className="main_loyout">
          <div className="dashboard">
            <h1>{_id ? "Edit Reel" : "Add Reel"}</h1>
            <Button
              className="btn btn_primary"
              onClick={() => {
                navigate("/manage-reels");
              }}
            >
              Back
            </Button>
          </div>
          <Card className="cards">
            <form onSubmit={formik.handleSubmit}>
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography className="custom_label">
                      Upload Reel
                    </Typography>
                    {profilePicture ? (
                      <div className="upload_image_preview">
                        <video
                          style={{ borderRadius: 0 }}
                          controls
                          src={profilePicture} // Replace profileVideo with your video source
                          width="200px"
                          height={"400px"} // You can adjust the width or use another styling as needed
                        />
                        <CancelIcon
                          style={{ marginTop: 0 }}
                          onClick={(e) => {
                            e.preventDefault();
                            setProfilePicture(""); // Reset the video when cancel is clicked
                          }}
                        />
                      </div>
                    ) : (
                      <Box className="upload_image">
                        <label htmlFor="icon-button-file_1">
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file_1"
                            type="file"
                            inputProps={{
                              accept: "video/mp4,video/x-m4v,video/*",
                            }}
                            onChange={handleImageUpload}
                          />
                          <Button component="span" className="upload_image_btn">
                            <img
                              style={{
                                borderRadius: 0,
                                height: "400px",
                                width: "200px",
                                border: "dotted 1px lightGray",
                              }}
                              src="/static/images/upload.png"
                              alt=""
                            />

                            <CameraAltIcon />
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography className="custom_label">Reel Title</Typography>
                    <TextField
                      hiddenLabel
                      type={"text"}
                      name="title"
                      variant="outlined"
                      fullWidth
                      value={formik.values.title}
                      placeholder="Enter title"
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={(val) => {
                        if (val.target.value === " ") {
                        } else {
                          formik.handleChange(val);
                        }
                      }}
                      helperText={
                        formik.touched.title && formik.errors.title
                          ? formik.errors.title.toString()
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography className="custom_label">
                      Reel Description
                    </Typography>
                    <TextField
                      hiddenLabel
                      type={"text"}
                      name="short_name"
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={3}
                      value={formik.values.short_name}
                      placeholder="Enter description"
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={(val) => {
                        if (val.target.value === " ") {
                        } else {
                          formik.handleChange(val);
                        }
                      }}
                      helperText={
                        formik.touched.short_name && formik.errors.short_name
                          ? formik.errors.short_name.toString()
                          : ""
                      }
                    />
                  </Grid>
                </Grid>
                <div className="form_btn">
                  <Button
                    size="large"
                    type="submit"
                    className="btn btn_primary"
                  >
                    Save
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AddReels;
